import {
  OrderProduct,
  OrderProductCpoConfig,
  OrderProductStatus,
} from "ts/api/order";
import { PaymentMethodType } from "ts/api/paymentMethod";
import {
  FullProduct,
  ProductImage,
  QuickOrderProductType,
} from "ts/api/product";
import { prettifyFileExtension } from "tools/file/prettifyFileExtension";
import {
  CustomProductOptionConfigType,
  ProductType,
} from "@evennt-tech/api-v2-core";
import { transformOrderProductCpoConfig } from "tools/custom-product-option/common";
import { customProductOptionShopStore } from "store/custom-product-option/custom-product-option-shop.store";
import { round } from "lodash";
import getProductSubCategory from "view/apps/Exhibitor/pages/Cart/helpers/getProductSubCategory";
import {
  calculateProductPaymentFeeAmount,
  calculateProductPlatformFeeAmount,
} from ".";

const getProductImage = (product: FullProduct): ProductImage => {
  const { imageId, image } = product;

  if (imageId) {
    const {
      productConfigsBySupplier,
    } = customProductOptionShopStore.getState();

    const images = productConfigsBySupplier
      .get(product.owner.id)
      ?.get(product.id)?.files;
    const selectedImage = images?.find((img) => img.id === imageId);

    if (selectedImage) {
      return {
        url: selectedImage.signedUrl,
        id: selectedImage.id,
        size: selectedImage.file.size,
        title: selectedImage.file.title,
        extension: prettifyFileExtension(
          selectedImage.file
        ) as ProductImage["extension"],
      };
    }
  }
  if (image) {
    return {
      url: image.signedUrl,
      id: image.id,
      size: image.file.size,
      title: image.file.title,
      extension: prettifyFileExtension(image.file) as ProductImage["extension"],
    };
  }

  return {
    extension: "jpeg",
    title: "title",
    size: 0,
    url: "",
    id: "",
  };
};
export const mapOrderProduct = (
  product: FullProduct & { invoiceId?: string },
  paymentMethodType: PaymentMethodType
): OrderProduct => {
  const orderImage = getProductImage(product);

  const extras = product?.extras?.length
    ? product.extras.map((e) => ({ ...e, quantity: round(e.quantity, 2) }))
    : [];
  const fees = product?.fees?.length ? product.fees : [];

  const paymentFeeAmount = calculateProductPaymentFeeAmount(product);
  const platformFeeAmount = calculateProductPlatformFeeAmount(product);
  const isApprovalRequired = product.approval;

  const selectedConfig = product.configs?.find(
    (c) => c.id === product.cpoConfigId
  );

  const orderProductCpoConfig: OrderProductCpoConfig | null = selectedConfig
    ? transformOrderProductCpoConfig(selectedConfig)
    : null;

  const subCategories = getProductSubCategory(product.categories);

  return {
    id: product.id,
    name: product.name,
    quantity: round(product.cartQuantity || 1, 2),
    price: product.price,
    priceWithFees: product.priceWithFees,
    surcharge: product.surcharge || null,
    paymentFeeAmount:
      paymentMethodType === PaymentMethodType.bankTransfer && paymentFeeAmount
        ? paymentFeeAmount
        : null,
    platformFeeAmount,
    approval: product?.approval,
    status: isApprovalRequired
      ? OrderProductStatus.reviewProcess
      : OrderProductStatus.received,
    image: orderImage,
    ...(!!product?.invoiceId && { invoiceId: product?.invoiceId }),
    owner: {
      id: product.owner.id,
      companyName: product.owner.name,
      organiserFee: product?.ownerToShow?.organiserFee ?? 0,
      logo: product?.owner?.logo?.url || "",
      contactPersonId: product.owner.contactPersonId,
    },
    type:
      product.type === ProductType.QUICK
        ? QuickOrderProductType.CUSTOM
        : QuickOrderProductType.SELECTED,
    notes: product.notes,
    categories: product.categories
      .map((category) => category?.mainCategory?.name)
      .filter((name): name is string => Boolean(name)),
    subCategories,
    extras,
    fees,
    ...(!!product.projectCode && {
      projectCode: product.projectCode,
    }),
    ...(product.cpoConfigId && { cpoConfigId: product.cpoConfigId }),
    ...(selectedConfig?.type === CustomProductOptionConfigType.CUSTOM && {
      isCustomConfig: true,
    }),
    ...(orderProductCpoConfig && { cpoConfig: orderProductCpoConfig }),
    ...(!!product.discountCodes?.length && {
      discountCodes: product.discountCodes,
    }),
    ...(product.discountAmountWithFees && {
      discountAmountWithFees: product.discountAmountWithFees,
    }),
    ...(product.discountAmount && { discountAmount: product.discountAmount }),
    ...(product.productId && { productId: product.productId }),
  };
};
